import { ArrowRight, Building2, ChevronRight, Download, Edit3, FileText, Hash, Lock, Mail, MapPin, MoreVertical, Phone, Plus, Search, Trash2, Unlock, User, UserCheck, UserPlus, Users2, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Client, ClientType } from '../../../types/models';
import { useSiretSearch, type CompanyData } from '@/hooks/useSiretSearch';
import { notify } from '../../../stores/notificationStore';
import { confirm } from '../../../stores/confirmStore';
import { CSVImport } from '@/components/CSVImport';
import { logger } from '../../../utils/logger';
import { useExportClients } from '../hooks/useExportClients';
import { ClientContactsManager } from '@/features/catalog/components/ClientContactsManager';
import { useQueryClient } from '@tanstack/react-query';
import { useClients, useCreateClient, useUpdateClient, useDeleteClient, useToggleClientStatus, clientsKeys } from '../hooks/useClients';

// ✅ FilterBar from ui (will be migrated to ui-v2 later)
import { FilterBar, type FilterOption } from '@/components/ui';

// ✅ MIGRATED TO V2 DESIGN SYSTEM - Sprint 4 (Catalog Management)
import {
  Button,
  IconButton,
} from '@/components/ui-v2';

interface ClientFormData {
  type: ClientType;
  fullName: string;
  position: string; // Fonction/titre affiché sur les documents
  companyName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  countryCode: string;
  sirenSiret: string;
  vatNumber: string;
  chorusProCode: string;
  chorusServiceCode: string;
  notes: string;
}

export const ClientsManagementResponsive: React.FC = () => {
  const { t } = useTranslation();

  // Pagination & search state (UI-driven, controls query params)
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatusFilters, setActiveStatusFilters] = useState<string[]>([]);

  // UI state
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [selectedClientForContacts, setSelectedClientForContacts] = useState<Client | null>(null);

  // Server data via TanStack Query (type filtering is server-side)
  const { data: clientsData, isLoading: loading } = useClients({
    page,
    limit: 20,
    search: searchTerm || undefined,
    activeOnly: false,
    clientType: activeStatusFilters.length > 0 ? activeStatusFilters : undefined,
  });

  const clients = clientsData?.clients ?? [];
  const pagination = clientsData?.pagination ?? { page: 1, limit: 20, total: 0, pages: 0 };

  // Mutations
  const queryClient = useQueryClient();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();
  const toggleClientStatus = useToggleClientStatus();

  const handleImportComplete = () => {
    queryClient.invalidateQueries({ queryKey: clientsKeys.all });
  };

  // Export CSV (architecture 3 couches : Composant → Hook → Service API)
  const { exportCSV, isExporting } = useExportClients();

  const handleExportCSV = () => {
    exportCSV({
      search: searchTerm || undefined,
      activeOnly: false,
      clientType: activeStatusFilters.length > 0 ? activeStatusFilters : undefined,
    });
  };

  // Recherche entreprise (par nom ou SIRET)
  const [searchMode, setSearchMode] = useState<'name' | 'siret'>('name');
  const [searchQuery, setSearchQuery] = useState('');
  const { searchBySiret, searchByName, clearResults, loading: searchLoading, results: searchResults } = useSiretSearch();

  const [formData, setFormData] = useState<ClientFormData>({
    type: 'INDIVIDUAL',
    fullName: '',
    position: '',
    companyName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zipCode: '',
    countryCode: 'FR',
    sirenSiret: '',
    vatNumber: '',
    chorusProCode: '',
    chorusServiceCode: '',
    notes: '',
  });

  // Form ref for submit button outside form
  const formRef = useRef<HTMLFormElement>(null);

  // Handlers pour FilterBar
  const handleStatusToggle = (status: string) => {
    setActiveStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActiveStatusFilters([]);
  };

  // Options de filtres pour les types de clients (server-side filtering)
  const statusFilterOptions: FilterOption[] = [
    {
      value: 'INDIVIDUAL',
      label: t('clients.type.INDIVIDUAL'),
      icon: User,
      color: 'blue',
    },
    {
      value: 'COMPANY',
      label: t('clients.type.COMPANY'),
      icon: Building2,
      color: 'purple',
    },
    {
      value: 'PUBLIC_ORG',
      label: t('clients.type.PUBLIC_ORG'),
      icon: MapPin,
      color: 'green',
    },
  ];

  const handleNewClient = () => {
    setEditingClient(null);
    setSearchQuery('');
    setSearchMode('name');
    clearResults();
    setFormData({
      type: 'INDIVIDUAL',
      fullName: '',
      position: '',
      companyName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      zipCode: '',
      countryCode: 'FR',
      sirenSiret: '',
      vatNumber: '',
      chorusProCode: '',
      chorusServiceCode: '',
      notes: '',
    });
    setShowForm(true);
  };

  // 🔍 Recherche entreprise (par nom ou SIRET)
  const handleCompanySearch = async () => {
    if (searchMode === 'name') {
      await searchByName(searchQuery);
    } else {
      const result = await searchBySiret(searchQuery);
      if (result) {
        selectCompany(result);
      }
    }
  };

  // Sélectionner une entreprise depuis les résultats
  const selectCompany = (company: CompanyData) => {
    setFormData(prev => ({
      ...prev,
      type: 'COMPANY',
      companyName: company.name,
      addressLine1: company.address,
      city: company.city,
      zipCode: company.postalCode,
      sirenSiret: company.siret,
      vatNumber: company.tvaNumber,
      email: company.email || prev.email,
      phone: company.phone || prev.phone,
    }));
    clearResults();
    setSearchQuery('');
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setFormData({
      type: client.type,
      fullName: client.fullName || '',
      position: client.position || '',
      companyName: client.companyName || '',
      email: client.email || '',
      phone: client.phone || '',
      addressLine1: client.addressLine1 || '',
      addressLine2: client.addressLine2 || '',
      city: client.city || '',
      zipCode: client.zipCode || '',
      countryCode: client.countryCode || 'FR',
      sirenSiret: client.sirenSiret || '',
      vatNumber: client.vatNumber || '',
      chorusProCode: client.chorusProCode || '',
      chorusServiceCode: client.chorusServiceCode || '',
      notes: client.notes || '',
    });
    setShowForm(true);
    setActiveMenu(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.type === 'INDIVIDUAL' && !formData.fullName.trim()) {
      notify.warning(t('clients.fullNameRequired'), t('clients.fieldRequired'));
      return;
    }

    if ((formData.type === 'COMPANY' || formData.type === 'PUBLIC_ORG') && !formData.companyName.trim()) {
      notify.warning(t('clients.companyNameRequired'), t('clients.fieldRequired'));
      return;
    }

    try {
      if (editingClient) {
        await updateClient.mutateAsync({ id: editingClient.id, data: formData });
        notify.success(t('clients.updateSuccess'), t('clients.updateSuccessTitle'));
      } else {
        await createClient.mutateAsync(formData);
        notify.success(t('clients.createSuccess'), t('clients.createSuccessTitle'));
      }

      setShowForm(false);
    } catch (error) {
      logger.error('Erreur sauvegarde client:', error);
      notify.error(t('clients.saveError'), t('clients.saveErrorTitle'));
    }
  };

  const handleDeleteClient = async (clientId: string, clientName: string) => {
    const confirmed = await confirm({
      title: t('clients.deleteTitle'),
      message: t('clients.deleteConfirm', { name: clientName }),
      confirmText: t('clients.delete'),
      cancelText: t('clients.cancel'),
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteClient.mutateAsync(clientId);
      notify.success(t('clients.deleteSuccess'), t('clients.deleteSuccessTitle'));
      setActiveMenu(null);
    } catch (error) {
      logger.error('Erreur suppression client:', error);
      notify.error(t('clients.deleteError'), t('clients.deleteErrorTitle'));
    }
  };

  const handleToggleStatus = async (clientId: string) => {
    try {
      await toggleClientStatus.mutateAsync(clientId);
      setActiveMenu(null);
    } catch (error) {
      logger.error('Erreur changement statut:', error);
      notify.error(t('clients.statusError'), t('clients.statusErrorTitle'));
    }
  };

  const handleOpenContacts = (client: Client) => {
    setSelectedClientForContacts(client);
    setShowContactsModal(true);
    setActiveMenu(null);
  };

  const handleCloseContacts = () => {
    setShowContactsModal(false);
    setSelectedClientForContacts(null);
  };

  const getClientDisplayName = (client: Client): string => {
    if (client.type === 'INDIVIDUAL') {
      return client.fullName || client.companyName || t('clients.noName');
    }
    return client.companyName || client.fullName || t('clients.noName');
  };

  return (
    <div className="px-8 pt-8 pb-12 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900 flex items-center gap-3">
            <UserCheck className="w-10 h-10 sm:w-12 sm:h-12 text-teal-600" />
            {t('clients.title')}
          </h1>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {t('clients.totalCount', { count: pagination.total })}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {clients.length > 0 && (
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={isExporting}
              icon={<Download className="w-4 h-4" />}
            >
              {isExporting ? t('common.export.exporting') : t('common.exportCSV')}
            </Button>
          )}
          <CSVImport entityType="clients" onImportComplete={handleImportComplete} />
          <Button
            onClick={handleNewClient}
            variant="secondary"
            icon={<Plus className="w-4 h-4" />}
          >
            {t('clients.newClient')}
          </Button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <FilterBar
        searchPlaceholder={t('clients.searchExtended')}
        searchValue={searchTerm}
        onSearchChange={(value: string) => { setSearchTerm(value); setPage(1); }}
        statusFilters={statusFilterOptions}
        activeStatuses={activeStatusFilters}
        onStatusToggle={handleStatusToggle}
        onClearFilters={handleClearFilters}
        totalItems={pagination.total}
        filteredItems={clients.length}
      />

      {/* Loading state */}
      {loading ? (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600">{t('clients.loadingClients')}</p>
          </div>
        </div>
      ) : clients.length === 0 ? (
        /* Empty state */
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-12 text-center border-2 border-dashed border-gray-300/50">
          <UserCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-600 mb-4">{t('clients.noClientsFound')}</p>
          <p className="text-gray-500 mb-6">
            {searchTerm || activeStatusFilters.length > 0
              ? t('clients.noClientsFilterDesc')
              : t('clients.createFirstDesc')}
          </p>
          <div className="flex justify-center">
            <Button
              onClick={handleNewClient}
              variant="secondary"
              size="lg"
              icon={<Plus className="w-5 h-5" />}
            >
              {t('clients.createClient')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Vue Mobile - Cartes */}
          <div className="md:hidden space-y-3">
            {clients.map((client) => {
              const displayName = getClientDisplayName(client);
              const icon = client.type === 'INDIVIDUAL' ? User : Building2;
              const IconComponent = icon;

              return (
                <div
                  key={client.id}
                  className="bg-white/20 backdrop-blur-xl border border-white/30 hover:bg-white/35 hover:shadow-2xl transition-all duration-300 rounded-3xl overflow-hidden"
                >
                  <div className="bg-white/10 backdrop-blur-sm p-4 border-b border-white/20">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <IconComponent className="w-5 h-5 text-gray-500" />
                          <h3 className="text-lg font-bold text-gray-900">{displayName}</h3>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          client.type === 'INDIVIDUAL'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          {client.type === 'INDIVIDUAL' ? t('clients.type.individual') : t('clients.type.professional')}
                        </span>
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => setActiveMenu(activeMenu === client.id ? null : client.id)}
                          className="p-2 hover:bg-white/30 hover:backdrop-blur-sm rounded-lg transition-all duration-300"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-600" />
                        </button>
                        {activeMenu === client.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setActiveMenu(null)}
                            />
                            <div className="absolute right-0 top-full mt-1 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 py-1 min-w-[160px] z-20">
                              <button
                                onClick={() => handleOpenContacts(client)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Users2 className="w-4 h-4" />
                                {t('clients.menu.contacts')}
                              </button>
                              <button
                                onClick={() => handleEditClient(client)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Edit3 className="w-4 h-4" />
                                {t('clients.menu.modify')}
                              </button>
                              <button
                                onClick={() => handleToggleStatus(client.id)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                {client.isActive ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                {client.isActive ? t('clients.status.deactivate') : t('clients.status.activate')}
                              </button>
                              <button
                                onClick={() => handleDeleteClient(client.id, displayName)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                {t('clients.menu.delete')}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    {client.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${client.email}`} className="hover:text-teal-600 truncate">
                          {client.email}
                        </a>
                      </div>
                    )}
                    {client.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`tel:${client.phone}`} className="hover:text-teal-600">
                          {client.phone}
                        </a>
                      </div>
                    )}
                    {client.city && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>{client.city}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Vue Desktop - Tableau */}
          <div className="hidden md:block bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/10 backdrop-blur-sm border-b border-white/20">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('clients.form.type')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('clients.fields.nameSociety')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('clients.fields.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('clients.fields.phone')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('clients.fields.city')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('clients.status.active')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('clients.fields.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {clients.map((client) => {
                    const displayName = getClientDisplayName(client);
                    const icon = client.type === 'INDIVIDUAL' ? User : Building2;
                    const IconComponent = icon;

                    return (
                      <tr key={client.id} className="hover:bg-white/10 transition-all duration-300">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <IconComponent className="w-5 h-5 text-gray-500" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{displayName}</div>
                          {client.type !== 'INDIVIDUAL' && client.sirenSiret && (
                            <div className="text-xs text-gray-500">SIRET: {client.sirenSiret}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {client.email ? (
                            <a href={`mailto:${client.email}`} className="hover:text-teal-600">
                              {client.email}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {client.phone || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {client.city || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            client.isActive ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {client.isActive ? t('clients.status.active') : t('clients.status.inactive')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <IconButton
                              onClick={() => handleOpenContacts(client)}
                              variant="teal"
                              size="sm"
                              aria-label={t('clients.contacts.manage')}
                            >
                              <Users2 className="w-4 h-4" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleEditClient(client)}
                              variant="teal"
                              size="sm"
                              aria-label={t('clients.menu.modify')}
                            >
                              <Edit3 className="w-4 h-4" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleToggleStatus(client.id)}
                              variant="gray"
                              size="sm"
                              aria-label={client.isActive ? t('clients.status.deactivate') : t('clients.status.activate')}
                            >
                              {client.isActive ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClient(client.id, displayName)}
                              variant="red"
                              size="sm"
                              aria-label={t('clients.menu.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white/60 rounded-xl hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('clients.pagination.previous')}
                </button>
                <span className="text-sm text-gray-600">
                  {t('clients.pagination.pageOf', { page: pagination.page, pages: pagination.pages })}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === pagination.pages}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white/60 rounded-xl hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('clients.pagination.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Formulaire Modal - Dashboard Design */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowForm(false)}
        >
          <div
            className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50/50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-teal-100 rounded-2xl">
                    <UserPlus className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingClient ? t('clients.edit') : t('clients.newClient')}
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {editingClient ? t('clients.updateInfo') : t('clients.addTooltip')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowForm(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* 🔍 Recherche entreprise */}
              {!editingClient && (
                <div className="px-6 pt-6 space-y-4">
                  <div className="p-5 bg-white/40 border border-white/50 rounded-2xl">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                        <Search className="w-4 h-4 text-teal-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">{t('clients.companySearch.title')}</h3>
                    </div>

                    {/* Toggle: Recherche par Nom / SIRET */}
                    <div className="grid grid-cols-2 gap-2 p-1.5 bg-gray-100 rounded-xl mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setSearchMode('name');
                          setSearchQuery('');
                          clearResults();
                        }}
                        className={`py-2 px-4 text-sm font-medium rounded-lg transition-all duration-300 ${
                          searchMode === 'name'
                            ? 'bg-white text-teal-700 shadow-md'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                        }`}
                      >
                        {t('clients.companySearch.byName')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSearchMode('siret');
                          setSearchQuery('');
                          clearResults();
                        }}
                        className={`py-2 px-4 text-sm font-medium rounded-lg transition-all duration-300 ${
                          searchMode === 'siret'
                            ? 'bg-white text-teal-700 shadow-md'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                        }`}
                      >
                        {t('clients.companySearch.bySiret')}
                      </button>
                    </div>

                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          const value = searchMode === 'siret'
                            ? e.target.value.replace(/\s/g, '').slice(0, 14)
                            : e.target.value;
                          setSearchQuery(value);
                        }}
                        placeholder={searchMode === 'name' ? t('clients.companySearch.namePlaceholder') : t('clients.companySearch.siretPlaceholder')}
                        className="flex-1 px-4 py-3 text-sm bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCompanySearch();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleCompanySearch}
                        disabled={searchLoading || !searchQuery.trim()}
                        className="px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
                      >
                        {searchLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            {t('clients.companySearch.searching')}
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4" />
                            {t('clients.companySearch.searchButton')}
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      {searchMode === 'name'
                        ? t('clients.companySearch.searchByNameHint')
                        : t('clients.companySearch.searchBySiretHint')}
                    </p>
                  </div>

                  {/* Résultats de recherche */}
                  {searchMode === 'name' && searchResults.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 max-h-64 overflow-y-auto">
                      <p className="text-sm font-medium text-gray-900 mb-3">
                        {t('clients.companySearch.resultCount', { count: searchResults.length })}
                      </p>
                      <div className="space-y-2">
                        {searchResults.map((company, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectCompany(company)}
                            className="w-full text-left p-4 bg-gray-50 border border-gray-100 rounded-xl hover:border-teal-400 hover:bg-white hover:shadow-md transition-all duration-300 group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate group-hover:text-teal-700 transition-colors">
                                  {company.name}
                                </p>
                                <p className="text-sm text-gray-600 truncate">
                                  {company.address}, {company.postalCode} {company.city}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  SIRET: {company.siret}
                                </p>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Form - Dashboard Design */}
            <form ref={formRef} onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations principales */}
                <div className="space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {t('clients.form.mainInfo')}
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('clients.form.type')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as ClientType })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    >
                      <option value="INDIVIDUAL">{t('clients.type.INDIVIDUAL')}</option>
                      <option value="COMPANY">{t('clients.type.company')}</option>
                      <option value="PUBLIC_ORG">{t('clients.type.PUBLIC_ORG')}</option>
                    </select>
                  </div>

                  {formData.type === 'INDIVIDUAL' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('clients.form.fullName')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder={t('clients.form.placeholders.fullName')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('clients.form.companyName')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        placeholder={t('clients.form.placeholders.companyName')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                  )}

                  {/* Fonction/titre affiché sur les documents (optionnel) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('clients.form.position')}
                    </label>
                    <textarea
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      placeholder={t('clients.form.positionPlaceholder')}
                      rows={2}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all resize-none"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('clients.positionHelp')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.email')}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('clients.form.placeholders.email')}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.phone')}</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder={t('clients.form.placeholders.phone')}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>
                </div>

                {/* Adresse */}
                <div className="space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {t('clients.form.address')}
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.addressLine1')}</label>
                    <input
                      type="text"
                      value={formData.addressLine1}
                      onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                      placeholder={t('clients.form.placeholders.addressLine1')}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.addressLine2')}</label>
                    <input
                      type="text"
                      value={formData.addressLine2}
                      onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                      placeholder={t('clients.form.addressLine2Placeholder')}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.zipCode')}</label>
                      <input
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                        placeholder={t('clients.form.placeholders.zipCode')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.city')}</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder={t('clients.form.placeholders.city')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.countryCode')}</label>
                    <input
                      type="text"
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value.toUpperCase() })}
                      placeholder={t('clients.form.placeholders.countryCode')}
                      maxLength={2}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all uppercase"
                    />
                  </div>
                </div>

                {/* Informations fiscales */}
                {formData.type !== 'INDIVIDUAL' && (
                  <div className="space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                    <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      {t('clients.form.fiscalInfo')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.sirenSiret')}</label>
                      <input
                        type="text"
                        value={formData.sirenSiret}
                        onChange={(e) => setFormData({ ...formData, sirenSiret: e.target.value })}
                        placeholder={t('clients.form.placeholders.sirenSiret')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.vatNumber')}</label>
                      <input
                        type="text"
                        value={formData.vatNumber}
                        onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                        placeholder={t('clients.form.placeholders.vatNumber')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    {formData.type === 'PUBLIC_ORG' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.chorusProCode')}</label>
                          <input
                            type="text"
                            value={formData.chorusProCode}
                            onChange={(e) => setFormData({ ...formData, chorusProCode: e.target.value })}
                            placeholder={t('clients.form.placeholders.chorusProCode')}
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.form.chorusServiceCode')}</label>
                          <input
                            type="text"
                            value={formData.chorusServiceCode}
                            onChange={(e) => setFormData({ ...formData, chorusServiceCode: e.target.value })}
                            placeholder={t('clients.form.placeholders.chorusServiceCode')}
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div className={`space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5 ${formData.type === 'INDIVIDUAL' ? 'md:col-span-2' : ''}`}>
                  <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {t('clients.form.notes')}
                  </h3>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    placeholder={t('clients.internalNotesPlaceholder')}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all resize-none"
                  />
                </div>
              </div>
            </form>
            </div>

            {/* Footer - Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50/50">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-3 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-all duration-300"
              >
                {t('clients.cancel')}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  if (formRef.current) {
                    formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                }}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2"
              >
                {editingClient ? t('clients.update') : t('clients.createClient')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestion des Contacts - Design V2 avec transparences */}
      {showContactsModal && selectedClientForContacts && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleCloseContacts}
        >
          <div
            className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-teal-50/50 to-white/80 backdrop-blur-xl border-b border-white/40 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                  <Users2 className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{t('clients.contacts.manage')}</h2>
              </div>
              <button
                onClick={handleCloseContacts}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white/40 rounded-xl"
                title={t('common.actions.close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <ClientContactsManager
                clientId={selectedClientForContacts.id}
                clientName={getClientDisplayName(selectedClientForContacts)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
